import { parseExpireMs } from 'src/utils/subscriptionExpire'

/**
 * 同一台设备会有多张订单（首单 + 历次续费单），所以按设备匹配到的是「一组」订单。
 * 约定：**最新的关联单 = 过期时间最晚的那一张**。
 * 过期时间解析不出来的单不参与比较（避免脏数据把结果带偏）；
 * 全部解析不出来时才退回数组第一张，保证不返回空。
 */
export const pickLatestExpireOrder = (orders = []) => {
  const list = Array.isArray(orders) ? orders : []
  if (!list.length) return null

  let best = null
  let bestMs = -Infinity
  list.forEach(row => {
    const ms = parseExpireMs(row?.expire_time)
    if (!Number.isFinite(ms)) return
    if (ms > bestMs) {
      bestMs = ms
      best = row
    }
  })

  return best || list[0] || null
}

const toKey = value => (value == null || value === '' ? null : String(value))

/**
 * 取「当前设备」的关联订单。
 *
 * 匹配优先级：
 *   0. `order_id`（设备记录 / DeviceUser 上的订单主键）→ 直接命中订单表的 `id`
 *      ✅ Msafely 后端 `user-device` 自 2026-09-17 起已返回 `order_id`，
 *      这是**最准**的一步：后端已经指明「这台设备当前是哪张单」，不需要再猜。
 *   1. `device_id`（订单表 device_id ↔ 设备记录 id）→ 命中多张时取**过期时间最晚**的那张
 *      （order_id 取不到时的兜底，例如早期写入的 DeviceUser 没有该字段）
 *   2. iCloud 账号 + 到期时间
 *   3. iCloud 账号
 *
 * 全部匹配不到返回 null —— 不能退回「全局最晚的一张」，
 * 否则会把别的设备（比如安卓单）的到期时间与档位显示到当前设备上。
 *
 * @param {Object} params
 * @param {Array} params.orders - user-account-order 列表
 * @param {Object} [params.deviceUser] - localStorage DeviceUser（含 order_id / device_id / username）
 * @param {Object} [params.device] - 设备接口（user-device）记录，优先于 deviceUser
 * @param {string|number} [params.orderId] - 已知订单 id（优先级最高）
 * @param {string|number} [params.deviceId] - 已知设备 id
 * @param {string} [params.iCloudUsername] - 已知 iCloud 账号
 * @returns {Object|null}
 */
export const resolveDeviceOrder = ({ orders, deviceUser, device, orderId, deviceId, iCloudUsername } = {}) => {
  const list = Array.isArray(orders) ? orders : []
  if (!list.length) return null

  // 0) 设备上的 order_id 直接对应订单主键（取不到就往下走）
  const targetOrderId =
    toKey(orderId) || toKey(device?.order_id ?? device?.orderId) || toKey(deviceUser?.order_id)
  if (targetOrderId) {
    const byOrderId = list.find(item => toKey(item?.id) === targetOrderId)
    if (byOrderId) return byOrderId
  }

  // 1) 按设备 id 找到一组订单，取过期时间最晚的那张
  const targetDeviceId =
    toKey(deviceId) || toKey(device?.id) || toKey(device?.device_id) || toKey(deviceUser?.device_id)
  if (targetDeviceId) {
    const byDevice = list.filter(item => toKey(item?.device_id) === targetDeviceId)
    if (byDevice.length) return pickLatestExpireOrder(byDevice)
  }

  // 2) 退化为 iCloud 账号（+ 到期时间更精确）
  const username = iCloudUsername || device?.iCloud_username || deviceUser?.username || deviceUser?.iCloud_username || null
  if (username) {
    const byAccount = list.filter(item => item?.icloud_account === username)
    if (byAccount.length) {
      const expireTime = device?.expire_time || deviceUser?.expire_time || null
      if (expireTime) {
        const exact = byAccount.filter(item => item?.expire_time === expireTime)
        if (exact.length) return pickLatestExpireOrder(exact)
      }

      return pickLatestExpireOrder(byAccount)
    }
  }

  return null
}

/**
 * Dashboard 卡片展示用的订阅信息：**设备接口优先，订单兜底**。
 *
 * 为什么以设备接口为准：`user-device` 现在直接返回
 * `order_id / product_name / order_level / expire_time / platform_type / subscription_status`，
 * 它就是「这台设备当前的订阅状态」，比从订单列表里挑一张更准、也更实时。
 * 订单仍用于补设备接口没有的字段（`merchant_no`（注册码行）/ `bill_card`（续费弹窗）等）。
 *
 * @param {Object} params
 * @param {Object} [params.order] - resolveDeviceOrder 得到的订单行
 * @param {Object} [params.device] - 设备接口（user-device）记录
 * @returns {Object} 可直接铺开给卡片用的对象
 */
export const resolveCardSubscription = ({ order, device } = {}) => {
  const base = order || {}
  if (!device) return base

  return {
    ...base,
    product_name: device.product_name ?? base.product_name,
    order_level: device.order_level ?? base.order_level,
    expire_time: device.expire_time ?? base.expire_time
  }
}

export default resolveDeviceOrder
